import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, XCircle, Users, BarChart3, MessageSquare, Calendar, Target, TrendingUp, Clock, Shield, Heart, Zap } from 'lucide-react';
import socialMediaImage from '@/assets/shooting-action.jpg';
import teamImage from '@/assets/team-shooting-1.jpg';

const ServiceSocialMedia = () => {
  const painPoints = [
    { problem: "You're posting randomly and hoping something sticks", solution: "We build a data-driven content calendar aligned with your business goals" },
    { problem: "You don't have time to create content AND run your business", solution: "We handle everything—strategy, creation, posting, and engagement" },
    { problem: "You've tried agencies before and felt like just another account number", solution: "You get a dedicated team who knows your name, your brand, and your vision" },
    { problem: "You're not sure what's working or what to post next", solution: "Monthly analytics reports with clear insights and actionable next steps" },
  ];

  const included = [
    { icon: Target, title: 'Custom Brand Strategy', desc: 'A tailored roadmap built around your goals, audience, and industry—not a cookie-cutter template.' },
    { icon: Calendar, title: 'Content Calendar & Scheduling', desc: 'Strategic posting schedules optimized for when your audience is most active.' },
    { icon: MessageSquare, title: 'Community Management', desc: 'We respond to comments, DMs, and engage with your audience like a real human—because we are.' },
    { icon: BarChart3, title: 'Analytics & Reporting', desc: 'Monthly deep-dives into what\'s working, what\'s not, and exactly what we\'re doing about it.' },
    { icon: Zap, title: 'Ad Campaign Management', desc: 'Targeted paid campaigns that put your brand in front of the right people at the right time.' },
    { icon: TrendingUp, title: 'Growth Strategy', desc: 'Proactive audience growth tactics—not just maintaining, but building momentum.' },
  ];

  const bigAgencyProblems = [
    'You\'re client #247 on a spreadsheet',
    'Generic templates reused across industries',
    'Monthly reports you can\'t understand',
    'A new account manager every 3 months',
    'Locked into 12-month contracts',
    'Charging $5K+/mo for cookie-cutter work',
  ];

  const vpmDifference = [
    'A dedicated team that knows your business by heart',
    'Custom strategy built for YOUR industry and audience',
    'Reports you actually understand with real action items',
    'The same faces, every month, invested in your success',
    'Flexible partnerships—we earn your trust, not lock you in',
    'Premium quality at a fraction of the cost',
  ];

  const faqs = [
    { q: "How quickly will I see results?", a: "Most clients see measurable engagement increases within the first 30 days. Significant lead generation typically kicks in by month 2-3 as we optimize your strategy based on real data." },
    { q: "Do I need to create any content myself?", a: "Absolutely not. We handle everything from strategy to creation to posting. All we need is occasional access to your business for photos/videos, and we take care of the rest." },
    { q: "What if I've been burned by agencies before?", a: "We hear this a lot. That's exactly why we do things differently—no long-term contracts, transparent reporting, and a dedicated team. We earn your business every single month." },
    { q: "Can I approve posts before they go live?", a: "Of course. We send everything for approval in advance. But most clients trust us enough within the first month to let us run with it—and they're glad they did." },
    { q: "What platforms do you manage?", a: "Instagram, Facebook, TikTok, LinkedIn, YouTube, and Google Business Profile. We'll recommend the platforms that make the most sense for your specific business and audience." },
    { q: "What's the investment?", a: "Our packages are tailored to your needs—but they're a fraction of what the big agencies charge. Book a discovery call and we'll give you a transparent quote with zero pressure." },
  ];

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHead
        title="Social Media Management Columbus Ohio | Best Social Media Agency | Vantage Point Media"
        description="Full-service social media management in Columbus Ohio. Custom strategy, content creation, community management, analytics & ad campaigns for med spas, dentists, financial advisors & professionals. No contracts."
        canonical="https://vantagepointmediagroup.com/services/social-media"
        keywords="social media management Columbus Ohio, social media agency Columbus Ohio, social media company Columbus, social media marketing Columbus Ohio, Instagram management Columbus, Facebook marketing Columbus Ohio"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Social Media Management Columbus Ohio",
          "provider": { "@type": "LocalBusiness", "name": "Vantage Point Media", "url": "https://vantagepointmediagroup.com" },
          "areaServed": { "@type": "State", "name": "Ohio" },
          "description": "Full-service social media management for businesses in Columbus Ohio. Custom strategy, content creation, community management, analytics reporting, and ad campaign management.",
          "url": "https://vantagepointmediagroup.com/services/social-media"
        }}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={socialMediaImage} alt="Social media management" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/90 to-foreground/75" />
          </div>
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/30 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-background text-xs sm:text-sm font-medium">Social Media Management</span>
              </span>
              <h1 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl text-background leading-tight mb-6">
                Stop Guessing.
                <span className="block text-primary mt-2">Start Growing.</span>
              </h1>
              <p className="text-base sm:text-xl text-background/85 max-w-2xl mb-8 leading-relaxed">
                Your competitors are showing up on social media every single day. The question isn't whether you need a social media strategy—it's how much longer you can afford not to have one.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="xl" className="group" asChild>
                  <a href="/booking">
                    Book Your Free Strategy Call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points → Solutions */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Sound Familiar?
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                These are the exact problems our clients had before working with us. Every single one is solvable.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {painPoints.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <div className="flex items-start gap-3 mb-4">
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/70 text-sm sm:text-base line-through decoration-destructive/40">{item.problem}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground font-medium text-sm sm:text-base">{item.solution}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">
                What You Get
              </span>
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Everything a $10K/mo Agency Offers.
                <span className="text-primary block mt-1">Without the $10K Price Tag.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {included.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big Agency vs VPM */}
        <section className="py-16 sm:py-24 bg-foreground text-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl mb-4">
                The Big Agency Problem
              </h2>
              <p className="text-background/70 text-base sm:text-lg">
                Large agencies charge premium prices for impersonal service. You deserve better.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
              {/* Big Agency Column */}
              <div className="bg-background/5 border border-background/10 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-destructive/20 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-display font-bold text-xl">The Big Agencies</h3>
                </div>
                <ul className="space-y-4">
                  {bigAgencyProblems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-1" />
                      <span className="text-background/70 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* VPM Column */}
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">BETTER WAY</div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Vantage Point Media</h3>
                </div>
                <ul className="space-y-4">
                  {vpmDifference.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                      <span className="text-background/90 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Trust / Partnership */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
              <div className="w-full lg:w-1/2">
                <img src={teamImage} alt="VPM team working with client" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-strong" />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">Why We're Different</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-4">
                  We Don't Do Clients.
                  <span className="text-primary block mt-1">We Do Partners.</span>
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  The big agencies see you as a number on a spreadsheet. We see you as a partner. Your wins are our wins. When your phone rings with a new lead from a post we created—that's the moment we live for.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Users, text: 'Dedicated team that knows your business inside and out' },
                    { icon: Clock, text: 'Same-day responses—because your time matters' },
                    { icon: Shield, text: 'No long-term contracts—we earn your trust monthly' },
                    { icon: Heart, text: 'We genuinely care about your growth, not just our invoice' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-foreground text-sm sm:text-base">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Questions? We've Got Answers.
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-card border border-border rounded-2xl shadow-soft group">
                  <summary className="flex items-center justify-between cursor-pointer p-5 sm:p-6 font-display font-semibold text-foreground text-sm sm:text-base">
                    {faq.q}
                    <span className="text-primary ml-4 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground text-sm sm:text-base leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-foreground relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-2xl sm:text-4xl text-background mb-4">
              Your Competitors Are Already Showing Up.
              <span className="text-primary block mt-2">Are You?</span>
            </h2>
            <p className="text-background/80 text-base sm:text-lg max-w-2xl mx-auto mb-8">
              Every day without a strategy is a day your competitors are winning clients that should be yours. Let's change that—starting with a free, no-pressure discovery call.
            </p>
            <Button variant="hero" size="xl" className="group w-full sm:w-auto" asChild>
              <a href="/booking">
                Book Your Free Discovery Call
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <p className="mt-4 text-background/50 text-xs sm:text-sm">No contracts. No pressure. Just a conversation about your growth.</p>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default ServiceSocialMedia;
